<template>
                <div v-if="currentView === 'log_analysis'">
                    <div class="page-header"><div><div class="page-title">日誌分析</div><div class="page-subtitle">分析郵件系統各服務的即時日誌</div></div></div>

                    <div class="card p-6 mb-6">
                        <div class="flex flex-wrap gap-4 items-end mb-4">
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">服務</label>
                                <select v-model="logFilter.service" class="input w-40">
                                    <option value="postfix">Postfix (SMTP)</option>
                                    <option value="rspamd">Rspamd (Anti-spam)</option>
                                    <option value="dovecot">Dovecot (IMAP/POP3)</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">關鍵字搜尋</label>
                                <input v-model="logFilter.search" type="text" placeholder="搜尋..."
                                    class="input w-64">
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">行數限制</label>
                                <select v-model="logFilter.lines" class="input w-32">
                                    <option value="100">100 行</option>
                                    <option value="500">500 行</option>
                                    <option value="1000">1000 行</option>
                                </select>
                            </div>
                            <button @click="fetchServiceLogs"
                                class="btn btn-primary">
                                查詢
                            </button>
                        </div>
                    </div>

                    <div class="card p-6">
                        <div class="overflow-x-auto">
                            <table class="data-table">
                                <thead>
                                    <tr class="border-b" style="background:var(--gray-100);">
                                        <th class="p-2 w-48">時間</th>
                                        <th class="p-2 w-32">服務</th>
                                        <th class="p-2">訊息內容</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="serviceLogs.length === 0">
                                        <td colspan="3" class="p-8 text-center" style="color:var(--gray-500);">
                                            尚無日誌資料
                                        </td>
                                    </tr>
                                    <tr v-for="(log, idx) in serviceLogs" :key="idx" class="border-b">
                                        <td class="p-2 whitespace-nowrap" style="color:var(--gray-600);">{{ log.timestamp }}</td>
                                        <td class="p-2 font-bold" style="color:var(--primary);">{{ log.service }}</td>
                                        <td class="p-2 break-all">{{ log.message }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- SSL View -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  serviceLogs,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  fetchServiceLogs,
} = store
</script>

